/**
 * DatasetDetailPage - Dataset detail view with 6 tabs
 * @module components/starnexus/datasets/DatasetDetailPage
 *
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ContentLayout from '@amzn/awsui-components-console/content-layout';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Tabs from '@amzn/awsui-components-console/tabs';
import Button from '@amzn/awsui-components-console/button';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import Spinner from '@amzn/awsui-components-console/spinner';
import Alert from '@amzn/awsui-components-console/alert';
import type { Dataset } from '../../../types/starnexus';
import { datasetService } from '../../../services/starnexus';
import { OverviewTab, SettingsTab, AndesAccessTab } from './tabs';
import { QuickUploadModal } from './QuickUploadModal';
import { BatchesTab } from './tabs/BatchesTab';
import { ExecutionStatusCard } from './ExecutionStatusCard';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../utils/apiClient';

const getStatusType = (status: string): 'success' | 'warning' | 'error' | 'info' | 'stopped' | 'pending' | 'in-progress' => {
  switch (status) {
    case 'active':
      return 'success';
    case 'processing':
      return 'in-progress';
    case 'draft':
      return 'pending';
    case 'archived':
      return 'stopped';
    case 'failed':
      return 'error';
    case 'andes_deleted':
      return 'warning';
    default:
      return 'info';
  }
};

export const DatasetDetailPage: React.FC = () => {
  const { datasetId } = useParams<{ datasetId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [batchesRefreshKey, setBatchesRefreshKey] = useState<number>(0);
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);

  // Auto-open QuickUploadModal if navigated here with ?action=upload (from list page row action).
  useEffect(() => {
    if (searchParams.get('action') === 'upload' && dataset?.status === 'active') {
      setUploadModalVisible(true);
      // Remove the query param so refresh doesn't reopen.
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, dataset?.status, setSearchParams]);

  useEffect(() => {
    if (!datasetId) return;
    let cancelled = false;
    apiFetch(apiUrl(`/api/executions/dataset/${encodeURIComponent(datasetId)}`), { ...apiFetchDefaults })
      .then(r => r.ok ? r.json() : { executions: [] })
      .then(data => {
        if (cancelled) return;
        const execs: any[] = Array.isArray(data.executions) ? data.executions : [];
        const active = execs.find((e: any) => e.status !== 'ready' && e.status !== 'failed');
        setActiveBatchId(active?.batchId || null);
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [datasetId, batchesRefreshKey]);

  // Debug log
  console.log('[DatasetDetailPage] Rendering with datasetId:', datasetId);

  useEffect(() => {
    const fetchDataset = async () => {
      console.log('[DatasetDetailPage] Fetching dataset:', datasetId);
      if (!datasetId) {
        setError('Dataset ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await datasetService.getDataset(datasetId);
        console.log('[DatasetDetailPage] Fetch result:', result);
        if (result) {
          setDataset(result);
        } else {
          setError('Dataset not found');
        }
      } catch (err) {
        console.error('[DatasetDetailPage] Fetch error:', err);
        setError('Failed to load dataset');
      } finally {
        setLoading(false);
      }
    };

    fetchDataset();
  }, [datasetId]);

  // Andes lazy health check: if dataset claims to be 'active' and has an Andes table,
  // verify Andes still holds it. If Andes reports DELETED/ARCHIVED/DEPRECATED or 404/410,
  // propagate status=andes_deleted to DDB so UI reflects reality on next load.
  // This catches external deletions (via DataCentral, lifecycle scripts, etc.) that our
  // active-batch polling never runs for.
  useEffect(() => {
    if (!dataset) return;
    if (dataset.status !== 'active') return;
    const providerName = dataset.providerName;
    const tableName = dataset.tableName;
    if (!providerName || !tableName) return;
    if (!dataset.andesTableArn) return;
    let cancelled = false;
    const checkAndes = async () => {
      try {
        const res = await apiFetch(apiUrl(`/api/andes/tables/status?providerName=${encodeURIComponent(providerName)}&tableName=${encodeURIComponent(tableName)}`), { ...apiFetchDefaults });
        if (cancelled) return;
        if (res.status === 404 || res.status === 410) {
          // Andes says it's gone.
          await markAndesDeleted(dataset.datasetId);
          if (!cancelled) setDataset(prev => prev ? { ...prev, status: 'andes_deleted' } : prev);
          return;
        }
        if (!res.ok) return; // other errors: silent, not fatal
        const data = await res.json();
        const deadStates = new Set(['DELETED', 'ARCHIVED', 'DEPRECATED']);
        if (data.state && deadStates.has(String(data.state).toUpperCase())) {
          await markAndesDeleted(dataset.datasetId);
          if (!cancelled) setDataset(prev => prev ? { ...prev, status: 'andes_deleted' } : prev);
        }
      } catch {
        // Silent: network error, skip this check.
      }
    };
    const markAndesDeleted = async (id: string) => {
      try {
        await apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(id)}/status`), {
      ...apiFetchDefaults,
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'andes_deleted' }),
        });
      } catch {
        // Silent: PATCH failed, will be retried next load.
      }
    };
    checkAndes();
    return () => { cancelled = true; };
  }, [dataset?.datasetId, dataset?.status, dataset?.providerName, dataset?.tableName, dataset?.andesTableArn]);

  const handleDatasetUpdate = (updatedDataset: Dataset) => {
    setDataset(updatedDataset);
  };

  if (loading) {
    return (
      <ContentLayout header={<Header variant="h1">Loading...</Header>}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
          <Spinner size="large" />
        </div>
      </ContentLayout>
    );
  }

  if (error || !dataset) {
    return (
      <ContentLayout header={<Header variant="h1">Dataset Not Found</Header>}>
        <Alert type="error" header="Error">
          {error || 'The requested dataset could not be found.'}
        </Alert>
        <div style={{ marginTop: '16px' }}>
          <Button onClick={() => navigate('/starnexus/datasets')}>Back to Datasets</Button>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      header={
          <Header
            variant="h1"
            info={<StatusIndicator type={getStatusType(dataset.status)}>{dataset.status}</StatusIndicator>}
            description={dataset.description}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => navigate('/starnexus/uploader/datasets')}>Back to list</Button>
              </SpaceBetween>
            }
          >
            {dataset.datasetName}
          </Header>
      }
    >
      {activeBatchId && (
        <div style={{ marginBottom: 16 }}>
          <ExecutionStatusCard
            datasetId={dataset.datasetId}
            batchId={activeBatchId}
            onUpdate={(s) => {
              if (s.status === 'ready' || s.status === 'failed') {
                // Clear when terminal so we don't keep polling.
                setActiveBatchId(null);
                setBatchesRefreshKey(k => k + 1);
              }
            }}
          />
        </div>
      )}
      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId)}
        tabs={[
          {
            id: 'overview',
            label: 'Overview',
            content: <OverviewTab dataset={dataset} />,
          },
          {
            id: 'batches',
            label: 'Batches',
            content: <BatchesTab datasetId={dataset.datasetId} refreshKey={batchesRefreshKey} onAppendClick={dataset.status === 'active' ? () => setUploadModalVisible(true) : undefined} />,
          },
          {
            id: 'andes-access',
            label: 'Andes access (coming soon)',
            content: (
              <AndesAccessTab
                datasetId={dataset.datasetId}
                hasAndesConfig={Boolean(dataset.config?.andesSettings?.provider && dataset.config?.andesSettings?.tableName)}
              />
            ),
          },
          {
            id: 'settings',
            label: 'Settings',
            content: <SettingsTab dataset={dataset} onUpdate={handleDatasetUpdate} />,
          },
        ]}
      />
      <QuickUploadModal
        visible={uploadModalVisible}
        dataset={dataset}
        onDismiss={() => {
          setUploadModalVisible(false);
          setBatchesRefreshKey(k => k + 1);
        }}
        onBatchStarted={() => {
          setBatchesRefreshKey(k => k + 1);
          setActiveTab('batches');
        }}
      />
    </ContentLayout>
  );
};

export default DatasetDetailPage;
